// ─── ProgramGenerationPanel.jsx ─────────────────────────────────────────
// Refonte écran Suivi — Phase 2 (UI). Voir docs/PLAN-REFONTE-ECRAN-SUIVI.md.
//
// La porte « prêt à publier » : checklist de complétude des 7 pièces du
// programme + préparation des pièces disponibles + accès aux workspaces +
// aperçu + clairance + publier (activé quand tout est prêt).
//
// DÉCOUPLÉ : ne persiste pas lui-même. Le parent fournit les callbacks
// (onSaveGenerated = sauvegarde les artefacts sur la consultation puis la
// rafraîchit ; onOpenPreview ; onPublish) et, optionnellement, le verdict de
// clairance déjà calculé. Composant NON encore greffé dans l'écran Suivi :
// le greffage dans ClientJourneyPage se fait avec vérification visuelle.

import { useState } from 'react';
import { computeProgramCompleteness, PIECE_STATUS } from '../services/programCompleteness';
import { generateMissingProgramPieces } from '../services/generateProgramPieces';
import { clearanceBadge } from '../services/clinicalClearance';

// La lettre reste préparée ici. Les recettes ont leur workspace unique.
const GENERATABLE_PIECES = new Set(['intro']);

// Libellés pour le message de la barre de chargement pendant l'appel IA.
const GEN_LABELS = { intro: "lettre d'intro" };

const COLORS = {
  ready: '#2E5E3E',
  todo: '#9a5b12',
  muted: '#8a877e',
  ink: '#2b2b28',
  line: 'rgba(0,0,0,0.10)',
  card: '#ffffff',
  soft: '#faf7f0',
  accentBg: '#eaf3ec',
};

function statusMeta(status) {
  switch (status) {
    case PIECE_STATUS.READY: return { label: 'Prêt', color: COLORS.ready, bg: COLORS.accentBg };
    case PIECE_STATUS.PARTIAL: return { label: 'Partiel', color: COLORS.todo, bg: '#f7efe0' };
    case PIECE_STATUS.TODO: return { label: 'À générer', color: COLORS.todo, bg: '#f7efe0' };
    default: return { label: '—', color: COLORS.muted, bg: COLORS.soft };
  }
}

export default function ProgramGenerationPanel({
  client,
  consultation,
  clearanceVerdict = null,
  deliveryChannel = 'app',
  onSaveGenerated,
  onOpenPreview,
  onPublish,
  onDeliverDocument,
}) {
  const [busy, setBusy] = useState(null); // null | 'all' | pieceKey
  const [busyLabel, setBusyLabel] = useState(''); // pièce IA en cours (barre)
  const [errors, setErrors] = useState([]);

  const completeness = computeProgramCompleteness(client, consultation);
  const { pieces, readyCount, applicableCount, allReady, missing } = completeness;
  const hasGeneratableMissing = missing.some((piece) => GENERATABLE_PIECES.has(piece));
  const badge = clearanceBadge(clearanceVerdict);
  const isDocumentDelivery = deliveryChannel === 'document';
  const documentStatus = badge.blocking
    ? {
      label: 'Remise document bloquée · validation clinique requise',
      color: '#8a2a22',
      border: 'rgba(138,42,34,.22)',
      background: '#fbeceb',
    }
    : badge.tone === 'unknown'
      ? {
        label: 'Remise document bloquée · clairance clinique non évaluée',
        color: '#8a2a22',
        border: 'rgba(138,42,34,.22)',
        background: '#fbeceb',
      }
    : allReady
      ? {
        label: 'Dossier prêt pour la remise par document',
        color: COLORS.ready,
        border: 'rgba(46,94,62,.22)',
        background: COLORS.accentBg,
      }
        : {
          label: `${missing.length} pièce${missing.length > 1 ? 's' : ''} à finaliser pour le document`,
          color: COLORS.todo,
          border: 'rgba(154,91,18,.20)',
          background: '#f7efe0',
        };

  async function runGeneration(only) {
    if (busy) return;
    setBusy(only ? only[0] : 'all');
    setBusyLabel('');
    setErrors([]);
    try {
      const res = await generateMissingProgramPieces(client, consultation, {
        ...(only ? { only } : {}),
        onProgress: ({ piece, phase }) => {
          if (phase === 'start') setBusyLabel(GEN_LABELS[piece] || '');
        },
      });
      if (res.generated && Object.keys(res.generated).length > 0 && typeof onSaveGenerated === 'function') {
        await onSaveGenerated(res.generated);
      }
      if (res.errors && res.errors.length > 0) setErrors(res.errors);
    } catch (e) {
      setErrors([{ piece: 'general', message: e?.message || String(e) }]);
    } finally {
      setBusy(null);
      setBusyLabel('');
    }
  }

  const pct = applicableCount > 0 ? Math.round((readyCount / applicableCount) * 100) : 0;

  return (
    <div style={{ background: COLORS.card, border: `1px solid ${COLORS.line}`, borderRadius: 12, overflow: 'hidden', fontFamily: 'inherit', color: COLORS.ink }}>
      <style>{'@keyframes bfcGenIndet { 0% { left: -40%; } 100% { left: 100%; } }'}</style>

      <div style={{ padding: '14px 16px', borderBottom: `1px solid ${COLORS.line}` }}>
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 12, flexWrap: 'wrap' }}>
          <div>
            <div style={{ fontSize: 15, fontWeight: 500 }}>
              Générer le programme{client?.prenom ? ` de ${client.prenom}` : ''}
            </div>
            <div style={{ fontSize: 12, color: COLORS.muted, marginTop: 1 }}>
              {isDocumentDelivery
                ? 'Prépare chaque pièce avant la remise du dossier par document'
                : 'Tout doit être prêt avant de publier vers son app'}
            </div>
          </div>
          {allReady ? (
            <span style={{ fontSize: 13, fontWeight: 500, color: COLORS.ready }}>Tout est prêt</span>
          ) : hasGeneratableMissing ? (
            <button
              type="button"
              onClick={() => runGeneration(null)}
              disabled={busy === 'all'}
              style={{ fontSize: 13, fontWeight: 500, padding: '9px 15px', borderRadius: 8, border: `1px solid ${COLORS.ready}`, background: COLORS.accentBg, color: COLORS.ready, cursor: busy ? 'default' : 'pointer', opacity: busy && busy !== 'all' ? 0.5 : 1 }}
            >
              {busy === 'all' ? 'Préparation…' : 'Préparer les éléments disponibles'}
            </button>
          ) : (
            <span style={{ fontSize: 12, color: COLORS.muted }}>À finaliser dans les espaces dédiés</span>
          )}
        </div>
        {busy ? (
          <div style={{ marginTop: 12 }}>
            <div style={{ position: 'relative', height: 6, borderRadius: 3, background: COLORS.soft, overflow: 'hidden' }}>
              <div style={{ position: 'absolute', top: 0, height: '100%', width: '40%', borderRadius: 3, background: COLORS.ready, animation: 'bfcGenIndet 1.1s ease-in-out infinite' }} />
            </div>
            <div style={{ fontSize: 12, color: COLORS.ready, marginTop: 6 }}>
              Génération en cours{busyLabel ? ` — ${busyLabel}` : ''}… cela peut prendre quelques secondes.
            </div>
          </div>
        ) : (
          <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginTop: 12 }}>
            <div style={{ flex: 1, height: 6, borderRadius: 3, background: COLORS.soft, overflow: 'hidden' }}>
              <div style={{ width: `${pct}%`, height: '100%', background: COLORS.ready }} />
            </div>
            <span style={{ fontSize: 12, color: COLORS.muted, whiteSpace: 'nowrap' }}>
              {readyCount} / {applicableCount} pièces prêtes
            </span>
          </div>
        )}
      </div>

      <div>
        {pieces.map((p, i) => {
          const meta = statusMeta(p.status);
          const canGenerate = GENERATABLE_PIECES.has(p.key) && (p.status === PIECE_STATUS.TODO || p.status === PIECE_STATUS.PARTIAL);
          const canOpenApplicationPackage = p.key === 'recipes'
            && (p.status === PIECE_STATUS.TODO || p.status === PIECE_STATUS.PARTIAL)
            && typeof onOpenPreview === 'function';
          const isNA = p.status === PIECE_STATUS.NA;
          return (
            <div key={p.key} style={{ display: 'flex', alignItems: 'center', gap: 12, padding: '11px 16px', borderBottom: i < pieces.length - 1 ? `1px solid ${COLORS.line}` : 'none', opacity: isNA ? 0.55 : 1 }}>
              <div style={{ flex: 1, minWidth: 0 }}>
                <div style={{ fontSize: 13 }}>{p.label}</div>
                {p.detail ? <div style={{ fontSize: 11, color: COLORS.muted }}>{p.detail}</div> : null}
              </div>
              <span style={{ fontSize: 11.5, padding: '3px 10px', borderRadius: 20, background: meta.bg, color: meta.color, whiteSpace: 'nowrap' }}>
                {meta.label}
              </span>
              {canGenerate ? (
                <button
                  type="button"
                  onClick={() => runGeneration([p.key])}
                  disabled={!!busy}
                  style={{ fontSize: 12, padding: '5px 11px', borderRadius: 8, border: `1px solid ${COLORS.line}`, background: 'transparent', color: COLORS.ink, cursor: busy ? 'default' : 'pointer', opacity: busy ? 0.5 : 1, whiteSpace: 'nowrap' }}
                >
                  {busy === p.key ? 'Génération…' : 'Générer'}
                </button>
              ) : canOpenApplicationPackage ? (
                <button
                  type="button"
                  onClick={onOpenPreview}
                  style={{ fontSize: 12, padding: '5px 11px', borderRadius: 8, border: `1px solid ${COLORS.line}`, background: 'transparent', color: COLORS.ink, cursor: 'pointer', whiteSpace: 'nowrap' }}
                >
                  Ouvrir Dossier application
                </button>
              ) : null}
            </div>
          );
        })}
      </div>

      {errors.length > 0 ? (
        <div style={{ padding: '10px 16px', background: '#fbeceb', borderTop: `1px solid ${COLORS.line}`, fontSize: 12, color: '#8a2a22' }}>
          {errors.map((e, i) => <div key={i}>Échec {e.piece} : {e.message}</div>)}
        </div>
      ) : null}

      <div style={{ padding: '12px 16px', background: COLORS.soft, borderTop: `1px solid ${COLORS.line}`, display: 'flex', alignItems: 'center', gap: 12, flexWrap: 'wrap' }}>
        <span style={{ fontSize: 12, color: badge.blocking ? '#8a2a22' : (badge.tone === 'ok' ? COLORS.ready : COLORS.muted) }}>
          {badge.label}
        </span>
        {!isDocumentDelivery && typeof onOpenPreview === 'function' ? (
          <button type="button" onClick={onOpenPreview} style={{ fontSize: 12, padding: '4px 0', border: 'none', background: 'transparent', color: COLORS.muted, cursor: 'pointer' }}>
            Aperçu app cliente
          </button>
        ) : null}
        <div style={{ marginLeft: 'auto', display: 'flex', alignItems: 'center', gap: 10 }}>
          {isDocumentDelivery ? (
            <>
              <span
                role="status"
                style={{
                  fontSize: 11.5,
                  color: documentStatus.color,
                  padding: '5px 10px',
                  borderRadius: 999,
                  border: `1px solid ${documentStatus.border}`,
                  background: documentStatus.background,
                }}
              >
                {documentStatus.label}
              </span>
              {allReady && badge.tone !== 'unknown' && !badge.blocking
                && typeof onDeliverDocument === 'function' ? (
                  <button
                    type="button"
                    onClick={onDeliverDocument}
                    style={{
                      fontSize: 13,
                      fontWeight: 600,
                      padding: '8px 14px',
                      borderRadius: 8,
                      border: `1px solid ${COLORS.ready}`,
                      background: COLORS.ready,
                      color: '#fff',
                      cursor: 'pointer',
                    }}
                  >
                    Préparer la remise documentaire
                  </button>
                ) : null}
            </>
          ) : !allReady ? (
            <span style={{ fontSize: 11.5, color: COLORS.todo }}>
              {missing.length} pièce{missing.length > 1 ? 's' : ''} à générer avant de publier
            </span>
          ) : null}
          {!isDocumentDelivery ? (
            <button
              type="button"
              onClick={onPublish}
              disabled={!allReady || badge.blocking}
              style={{ fontSize: 13, fontWeight: 500, padding: '8px 14px', borderRadius: 8, border: `1px solid ${allReady && !badge.blocking ? COLORS.ready : COLORS.line}`, background: allReady && !badge.blocking ? COLORS.ready : 'transparent', color: allReady && !badge.blocking ? '#fff' : COLORS.muted, cursor: allReady && !badge.blocking ? 'pointer' : 'default' }}
            >
              Publier le programme
            </button>
          ) : null}
        </div>
      </div>
    </div>
  );
}
